/**
 * Reusable server-side filter: button with badge (applied count) opens a dropdown
 * built from a config array. Parent passes value/defaultValue and receives params
 * only on Apply. Use by defining a filters array and mapping emitted params to your API.
 *
 * @example
 * const filters = [
 *   { type: "multiSelect", paramKey: "status", label: "Status", options: [...] },
 *   { type: "booleanPair", paramKey: "isRoot", label: "Type", trueLabel: "Root", falseLabel: "Non-root" },
 * ];
 * <ServerSideFilter filters={filters} value={params} defaultValue={defaultParams} onChange={setParamsAndFetch} />
 */

import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Popover,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react-v3";
import React, { useCallback, useEffect, useState } from "react";

/** Chakra Popover compound types omit children and some DOM/style props; wrappers accept them. */
type TriggerProps = React.ComponentProps<typeof Popover.Trigger> & {
  asChild?: boolean;
  children?: React.ReactNode;
};
const PopoverTriggerWithChild = Popover.Trigger as React.ForwardRefExoticComponent<TriggerProps>;
const PopoverPositionerWithChild = Popover.Positioner as React.ForwardRefExoticComponent<
  React.PropsWithChildren<Record<string, unknown>>
>;
const PopoverContentWithChild = Popover.Content as React.ForwardRefExoticComponent<
  React.PropsWithChildren<Record<string, unknown>>
>;

/** Ensures unchecked checkbox control shows a visible empty box (border). */
const checkboxControlBorderStyles = {
  "& [data-part=control]": {
    borderWidth: "1px",
    borderColor: "border.emphasized",
    borderStyle: "solid",
  },
} as const;

export type FilterDefMultiSelect = {
  type: "multiSelect";
  paramKey: string;
  label: string;
  options: { value: string; label: string }[];
};

export type FilterDefBooleanPair = {
  type: "booleanPair";
  paramKey: string;
  label: string;
  trueLabel: string;
  falseLabel: string;
};

export type FilterDef = FilterDefMultiSelect | FilterDefBooleanPair;

function countAppliedFilters(value: Record<string, unknown>, filters: FilterDef[]): number {
  let count = 0;
  for (const def of filters) {
    const v = value[def.paramKey];
    if (def.type === "multiSelect") {
      if (Array.isArray(v) && v.length > 0) count += 1;
    } else if (def.type === "booleanPair") {
      if (v === true || v === false) count += 1;
    }
  }
  return count;
}

function shallowCopy(value: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(value)) out[k] = value[k];
  return out;
}

function isAllSelected(def: FilterDef, val: unknown): boolean {
  if (def.type === "multiSelect") {
    if (!Array.isArray(val)) return false;
    return val.length === def.options.length;
  }
  if (def.type === "booleanPair") return val === undefined;
  return false;
}

function buildEmittedParams(
  draft: Record<string, unknown>,
  defaultValue: Record<string, unknown>,
  filters: FilterDef[]
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const def of filters) {
    const raw = def.paramKey in draft ? draft[def.paramKey] : defaultValue[def.paramKey];
    out[def.paramKey] = isAllSelected(def, raw) ? undefined : raw;
  }
  return out;
}

function paramsEqual(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  filters: FilterDef[]
): boolean {
  for (const def of filters) {
    const va = def.paramKey in a ? a[def.paramKey] : undefined;
    const vb = def.paramKey in b ? b[def.paramKey] : undefined;
    const aAll = def.type === "multiSelect" && isAllSelected(def, va);
    const bAll = def.type === "multiSelect" && isAllSelected(def, vb);
    if (aAll || bAll) {
      if (aAll !== bAll) return false;
      continue;
    }
    if (Array.isArray(va) && Array.isArray(vb)) {
      const sortedA = [...va].sort();
      const sortedB = [...vb].sort();
      if (sortedA.length !== sortedB.length) return false;
      for (let i = 0; i < sortedA.length; i++) if (sortedA[i] !== sortedB[i]) return false;
    } else if (va !== vb) return false;
  }
  return true;
}

export function ServerSideFilter({
  filters,
  value,
  defaultValue,
  onChange,
  isDisabled,
}: {
  filters: FilterDef[];
  value: Record<string, unknown>;
  defaultValue: Record<string, unknown>;
  onChange: (params: Record<string, unknown>) => void;
  isDisabled?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, unknown>>(shallowCopy(value));

  useEffect(() => {
    if (isOpen) setDraft(buildEmittedParams(value, defaultValue, filters));
  }, [isOpen, value, defaultValue, filters]);

  const setDraftKey = useCallback(function setDraftKey(key: string, val: unknown) {
    setDraft(prev => {
      const next = { ...prev };
      next[key] = val;
      return next;
    });
  }, []);

  const handleReset = useCallback(
    function handleReset() {
      setDraft(shallowCopy(defaultValue));
    },
    [defaultValue]
  );

  const handleApply = useCallback(
    function handleApply() {
      onChange(buildEmittedParams(draft, defaultValue, filters));
      setIsOpen(false);
    },
    [draft, defaultValue, filters, onChange]
  );

  const appliedCount = countAppliedFilters(value, filters);
  const emittedDraft = buildEmittedParams(draft, defaultValue, filters);
  const hasDraftChanges = !paramsEqual(emittedDraft, value, filters);

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(details: { open: boolean }) => setIsOpen(details.open)}
      onInteractOutside={() => setIsOpen(false)}
      positioning={{ placement: "bottom-start" }}
    >
      <PopoverTriggerWithChild asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isDisabled}
          bg="bg.surface"
          color="fg.emphasized"
          borderWidth="1px"
          borderColor="border.emphasized"
          paddingX={2}
          paddingY={2}
          borderRadius="md"
        >
          Filters
          {appliedCount > 0 && (
            <Badge ml={2} colorPalette="blue" borderRadius="full">
              {appliedCount}
            </Badge>
          )}
        </Button>
      </PopoverTriggerWithChild>
      <PopoverPositionerWithChild zIndex={1500}>
        <PopoverContentWithChild
          width="auto"
          minW="240px"
          bg="bg.surface"
          borderColor="border"
          borderWidth="1px"
          borderRadius="md"
          boxShadow="md"
        >
          <Popover.Body>
            <Box>
              <VStack align="stretch" gap={4}>
                {filters.map(def => {
                  if (def.type === "multiSelect") {
                    const raw = draft[def.paramKey] as string[] | undefined;
                    const selected =
                      raw === undefined || raw.length === 0 ? def.options.map(o => o.value) : raw;
                    return (
                      <Box key={def.paramKey}>
                        <Text fontSize="sm" fontWeight="bold" mb={2}>
                          {def.label}
                        </Text>
                        <Stack gap={1} pl={2}>
                          {def.options.map(opt => (
                            <Checkbox.Root
                              key={opt.value}
                              size="sm"
                              colorPalette="blue"
                              checked={selected.includes(opt.value)}
                              onCheckedChange={(details: { checked: boolean }) => {
                                const next = details.checked
                                  ? [...selected, opt.value]
                                  : selected.filter(x => x !== opt.value);
                                setDraftKey(def.paramKey, next);
                              }}
                              css={checkboxControlBorderStyles}
                            >
                              <Checkbox.Control />
                              <Checkbox.HiddenInput />
                              <Text as="span" ml={2}>
                                {opt.label}
                              </Text>
                            </Checkbox.Root>
                          ))}
                        </Stack>
                      </Box>
                    );
                  }
                  if (def.type === "booleanPair") {
                    const pairVal = draft[def.paramKey] as boolean | undefined;
                    const checkedTrue = pairVal === true || pairVal === undefined;
                    const checkedFalse = pairVal === false || pairVal === undefined;
                    return (
                      <Box key={def.paramKey}>
                        <Text fontSize="sm" fontWeight="bold" mb={2}>
                          {def.label}
                        </Text>
                        <Stack gap={1} pl={2}>
                          <Checkbox.Root
                            size="sm"
                            colorPalette="blue"
                            checked={checkedTrue}
                            onCheckedChange={(details: { checked: boolean }) => {
                              if (details.checked)
                                setDraftKey(def.paramKey, checkedFalse ? undefined : true);
                              else setDraftKey(def.paramKey, checkedFalse ? false : undefined);
                            }}
                            css={checkboxControlBorderStyles}
                          >
                            <Checkbox.Control />
                            <Checkbox.HiddenInput />
                            <Text as="span" ml={2}>
                              {def.trueLabel}
                            </Text>
                          </Checkbox.Root>
                          <Checkbox.Root
                            size="sm"
                            colorPalette="blue"
                            checked={checkedFalse}
                            onCheckedChange={(details: { checked: boolean }) => {
                              if (details.checked)
                                setDraftKey(def.paramKey, checkedTrue ? undefined : false);
                              else setDraftKey(def.paramKey, checkedTrue ? true : undefined);
                            }}
                            css={checkboxControlBorderStyles}
                          >
                            <Checkbox.Control />
                            <Checkbox.HiddenInput />
                            <Text as="span" ml={2}>
                              {def.falseLabel}
                            </Text>
                          </Checkbox.Root>
                        </Stack>
                      </Box>
                    );
                  }
                  return null;
                })}
                <Flex gap={4} justify="flex-end" pt={3}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorPalette="gray"
                    onClick={handleReset}
                    bg="bg.surface"
                    borderWidth="1px"
                    borderColor="border.emphasized"
                    color="fg.emphasized"
                    px={3}
                    py={1.5}
                    borderRadius="md"
                  >
                    Reset
                  </Button>
                  <Button
                    size="sm"
                    variant="solid"
                    colorPalette="blue"
                    onClick={handleApply}
                    disabled={!hasDraftChanges}
                    bg="blue.500"
                    color="white"
                    borderWidth="0"
                    px={3}
                    py={1.5}
                    borderRadius="md"
                  >
                    Apply
                  </Button>
                </Flex>
              </VStack>
            </Box>
          </Popover.Body>
        </PopoverContentWithChild>
      </PopoverPositionerWithChild>
    </Popover.Root>
  );
}
